import { Link } from "react-router-dom";
import { useRef, useState, useEffect, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { SectionHeader } from "@/components/ui/SectionHeader";

// Lazy load the map component to defer Leaflet (~41KB) until needed
const ServiceAreaMap = lazy(() => import("@/components/ServiceAreaMap"));

// Service areas with links for SEO accessibility
const serviceAreaLinks = [
  { name: "The Woodlands", href: "/roofing-the-woodlands-tx" },
  { name: "Kingwood", href: "/roofing-kingwood-tx" },
  { name: "Humble", href: "/roofing-humble-tx" },
  { name: "Atascocita", href: "/roofing-atascocita-tx" },
  { name: "Spring", href: "/roofing-spring-tx" },
  { name: "Conroe", href: "/roofing-conroe-tx" },
  { name: "Aldine", href: "/roofing-aldine-tx" },
];

const LocalAuthoritySection = () => {
  const { t } = useTranslation('home');
  const [mapVisible, setMapVisible] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mapObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setMapVisible(true);
      },
      { threshold: 0.1 }
    );
    if (mapRef.current) mapObserver.observe(mapRef.current);
    return () => {
      mapObserver.disconnect();
    };
  }, []);

  return (
    <section className="bg-navy on-navy relative overflow-hidden py-24 md:py-32">
      {/* Map background */}
      <div ref={mapRef} className="absolute inset-0 opacity-50">
        {mapVisible && (
          <Suspense fallback={<div className="h-full w-full bg-navy" />}>
            <ServiceAreaMap />
          </Suspense>
        )}
      </div>

      {/* Legibility scrim over the map */}
      <div className="absolute inset-0 bg-gradient-to-b from-navy/40 via-transparent to-navy/60" />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          tone="navy"
          as="h2"
          headline={
            <>
              {t('localAuthority.headlinePart1')} <br />
              <span className="text-white">{t('localAuthority.headlinePart2')}</span>
            </>
          }
          headlineClassName="lg:text-6xl"
          className="mb-10"
        />

        {/* Service area chips */}
        <motion.div
          className="mb-10 flex flex-wrap gap-3"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {serviceAreaLinks.map((area, index) => (
            <Link
              key={index}
              to={area.href}
              className="rounded-full border border-white/40 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/10"
            >
              {area.name}
            </Link>
          ))}
        </motion.div>

        {/* Subtext */}
        <motion.p
          className="text-on-navy-small text-base font-medium"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {t('localAuthority.subtext')}
        </motion.p>
      </div>
    </section>
  );
};

export default LocalAuthoritySection;
